import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ChartBarIcon, UsersIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const Analytics = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({ stats: {}, byStatus: [], byServiceType: [], claimantsCount: 0 });
  const [usage, setUsage] = useState({ byMonth: [] });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [ov, us] = await Promise.all([
          makeAuthenticatedRequest('/analytics/overview'),
          makeAuthenticatedRequest('/analytics/usage')
        ]);
        if (ov.success) setOverview(ov.data);
        if (us.success) setUsage(us.data);
      } catch (_) {
        // handled by toast in makeAuthenticatedRequest
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [makeAuthenticatedRequest]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl shadow-lg p-8 text-white">
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="text-indigo-100">Overview of your activity and trends</p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Appointments</p>
              <p className="text-2xl font-bold">{overview.stats.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold">{overview.stats.completed || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-amber-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold">{overview.stats.upcoming || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Claimants</p>
              <p className="text-2xl font-bold">{overview.claimantsCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Appointments by Status</h2>
          <ul className="divide-y divide-gray-200">
            {(overview.byStatus || []).map((row) => (
              <li key={row.status} className="py-2 flex justify-between">
                <span className="text-gray-700 capitalize">{row.status.replaceAll('_',' ')}</span>
                <span className="font-semibold">{row.count}</span>
              </li>
            ))}
            {(!overview.byStatus || overview.byStatus.length === 0) && (
              <li className="text-sm text-gray-500">No data</li>
            )}
          </ul>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Types</h2>
          <ul className="divide-y divide-gray-200">
            {(overview.byServiceType || []).map((row) => (
              <li key={row.service_type} className="py-2 flex justify-between">
                <span className="text-gray-700">{row.service_type}</span>
                <span className="font-semibold">{row.count}</span>
              </li>
            ))}
            {(!overview.byServiceType || overview.byServiceType.length === 0) && (
              <li className="text-sm text-gray-500">No data</li>
            )}
          </ul>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage Over Time</h2>
        <div className="space-y-2">
          {(usage.byMonth || []).map((row) => (
            <div key={row.month} className="flex justify-between text-sm">
              <span className="text-gray-600">{row.month}</span>
              <span className="font-medium">{row.count}</span>
            </div>
          ))}
          {(!usage.byMonth || usage.byMonth.length === 0) && (
            <p className="text-sm text-gray-500">No usage data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;


